import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, FormField, Modal } from '@react-apps/design-system';
import { extractApiErrorStatus } from '@react-apps/auth';
import { Textarea } from '@/shared/components/textarea';
import type { ImpersonationTarget } from './impersonation.store';
import {
  startImpersonationSession,
  type StartedImpersonation,
} from './impersonation-api';

const REASON_MIN = 3;
const REASON_MAX = 500;

/** Mensagem fixa por status: a da API é técnica e em inglês. */
function describeStartError(err: unknown): string {
  switch (extractApiErrorStatus(err)) {
    case 403:
      return 'Você não tem permissão para atuar como esta pessoa.';
    case 404:
      return 'Esta pessoa não tem perfil ativo nesta plataforma.';
    default:
      return 'Não foi possível iniciar. Tente de novo.';
  }
}

/**
 * Pede o motivo antes de abrir a sessão de suporte — ele vai para a trilha de
 * auditoria da conta. Monte-o só quando for abrir: o motivo começa vazio a cada
 * vez, e enquanto a sessão está sendo aberta o modal não fecha.
 *
 * Devolve a sessão sem vesti-la: quem chama decide se a máscara vale nesta aba
 * (`scopeId` omitido, o próprio painel) ou vai para a aba de outra plataforma.
 */
export function ImpersonateModal({
  target,
  scopeId,
  onClose,
  onStarted,
}: {
  target: ImpersonationTarget;
  scopeId?: string;
  onClose: () => void;
  onStarted: (session: StartedImpersonation) => void;
}) {
  const fieldId = useId();
  const [reason, setReason] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const start = useMutation({
    mutationFn: (trimmed: string) =>
      startImpersonationSession(target.userId, trimmed, scopeId),
    onSuccess: (session) => onStarted(session),
  });

  const length = reason.trim().length;
  const valid = length >= REASON_MIN && length <= REASON_MAX;
  const validationError =
    showValidation && !valid
      ? `O motivo precisa ter de ${REASON_MIN} a ${REASON_MAX} caracteres (agora: ${length}).`
      : undefined;
  const busy = start.isPending;
  const close = () => {
    if (!busy) onClose();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      setShowValidation(true);
      return;
    }
    start.mutate(reason.trim());
  };

  return (
    <Modal
      open
      onClose={close}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
      title={`Atuar como ${target.name}`}
      description="A sessão dura 1 hora. Toda requisição sai com a identidade dessa pessoa, e o motivo fica registrado no histórico da conta."
    >
      <form onSubmit={submit} noValidate>
        <FormField
          label="Motivo"
          htmlFor={fieldId}
          required
          error={validationError}
          helperText={`${length}/${REASON_MAX}`}
        >
          <Textarea
            id={fieldId}
            value={reason}
            onChange={(value) => {
              setReason(value);
              // O erro de envio era do motivo anterior.
              if (start.isError) start.reset();
            }}
            disabled={busy}
            placeholder="Ex.: conferir por que a aula não aparece para o aluno (chamado 1234)"
            invalid={!!validationError}
            describedBy={
              validationError ? `${fieldId}-error` : `${fieldId}-helper`
            }
          />
        </FormField>
        {start.isError && (
          <p role="alert" className="text-error-600 mt-3 text-sm">
            {describeStartError(start.error)}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={close}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={busy}>
            {busy && (
              <span
                aria-hidden="true"
                className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
            )}
            {busy ? 'Iniciando…' : 'Atuar como'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
